import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import Button from '@carrier-io/air-react/Button';
import Stack from '@carrier-io/air-react/Stack';
import {
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@carrier-io/air-react';
import SearchBox from '@carrier-io/air-react/patterns/SearchBox';
import Table from '@carrier-io/air-react/Table';
import Box from '@carrier-io/air-react/Box';
import { BaseLayout } from 'layouts/BaseLayout';
import { StatBox } from 'components/StatBox';
import VendorDefLogoIcon from 'components/icons/vendor-deflogo-icon';
import { Loader } from 'components/Loader';

import { MapStatusToComponent } from 'utils/mappers';
import { isIdValid } from 'utils/validators';
import { calcPublishedMessages } from 'utils/calcPublishedMessages';
import { useHandleError } from 'hooks/useHandleError';

import { OutletRoute, PrivateRoute } from 'constants/routes';
import { useAuth } from 'context/AuthContext';
import {
  DataProducerProject,
  isConnectionButtonShown,
  isProjectExpired,
  isProjectOnboarding,
  ProjectOnboardingStatus,
} from 'store/models/Project';
import { Vendor } from 'store/models/models';
import { VendorStats } from 'store/models/Stats';
import { UserRoles } from 'store/models/User';
import { GET_VENDORS_BY_REPRESENTATIVE_ID } from 'store/graphql/queries/GET_VENDORS_BY_REPRESENTATIVE_ID';
import { GET_COUNT_OF_REGISTERED_DEVICES } from 'store/graphql/queries/GET_COUNT_OF_REGISTERED_DEVICES';
import { GET_COUNT_OF_PUBLISHED_MESSAGES } from 'store/graphql/queries/GET_COUNT_OF_PUBLISHED_MESSAGES';
import { GET_ERROR_MESSAGES_FOR_PROJECT } from 'store/graphql/queries/GET_ERROR_MESSAGES_FOR_PROJECT';

import styles from '../../styles/Dashboard.module.css';

const VendorDashboard = () => {
  const navigate = useNavigate();
  const { currentUserRoles, currentUser, logout } = useAuth();
  const isTeamLead = currentUserRoles?.includes(UserRoles.VENDOR_TL);
  const [vendorData, setVendorData] = useState<Vendor | null>(null);
  const [rawProjectsList, setRawProjectsList] = useState<DataProducerProject[]>([]);
  const [projectsSearch, setProjectsSearch] = useState<string>('');
  const [vendorStats, setVendorStats] = useState<VendorStats | null>(null);

  const [currPage, setCurrPage] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(5);

  const filteredProjects = useMemo(
    () => rawProjectsList.filter((vendor) => vendor.name.toLowerCase().includes(projectsSearch.toLowerCase())),
    [rawProjectsList, projectsSearch],
  );

  const paginatedList = useMemo(
    () => filteredProjects.slice(currPage * itemsPerPage, (currPage + 1) * itemsPerPage),
    [itemsPerPage, currPage, filteredProjects],
  );

  const {
    loading: loadingUser,
    data: dataUser,
    error: errorUser,
  } = useQuery(GET_VENDORS_BY_REPRESENTATIVE_ID, {
    variables: { representativeId: currentUser?.username },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !currentUser?.username,
  });

  const { loading: devicesStatsLoading, data: devicesStatsData } = useQuery(GET_COUNT_OF_REGISTERED_DEVICES, {
    variables: { dataContractName: vendorData?.vendorName, fuzzySearch: true },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !vendorData?.vendorName,
  });

  const { loading: messagesStatsLoading, data: messagesStatsData } = useQuery(GET_COUNT_OF_PUBLISHED_MESSAGES, {
    variables: { dataContractName: vendorData?.vendorName, fuzzySearch: true },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !vendorData?.vendorName,
  });

  const { data: errorStatsData, loading: errorStatsLoading } = useQuery(GET_ERROR_MESSAGES_FOR_PROJECT, {
    variables: {
      input: {
        dataContractName: vendorData?.vendorName,
        fuzzySearch: true,
      },
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !vendorData?.vendorName,
  });

  const isLoaded =
    !!currentUser?.username &&
    !!vendorData?.vendorName &&
    !loadingUser &&
    !errorStatsLoading &&
    !devicesStatsLoading &&
    !messagesStatsLoading;

  const completeConnection = (projectId: string, projectStatus: ProjectOnboardingStatus) => {
    switch (projectStatus) {
      case ProjectOnboardingStatus.INFRA_SPINED:
        navigate(
          `/vendor/${currentUser?.username}/project/${projectId}${PrivateRoute.Connection}/${OutletRoute.AddCertificate}`,
        );
        break;
      case ProjectOnboardingStatus.CERT_ADDED:
        navigate(
          `/vendor/${currentUser?.username}/project/${projectId}${PrivateRoute.Connection}/${OutletRoute.SetUpScript}`,
        );
        break;
      case ProjectOnboardingStatus.CONNECTED:
        navigate(
          `/vendor/${currentUser?.username}/project/${projectId}${PrivateRoute.Connection}/${OutletRoute.TestPayload}`,
        );
        break;
      default:
        navigate(`/vendor/${currentUser?.username}/project/${projectId}${PrivateRoute.Connection}`);
        break;
    }
  };

  const getVendorAddress = () => {
    let addressValue = vendorData ? `${vendorData.contactInfo.street}, ${vendorData.contactInfo.city}, ` : '';

    if (vendorData && vendorData.contactInfo?.state) {
      addressValue += `${vendorData.contactInfo?.state}, `;
    }

    if (vendorData && vendorData.contactInfo?.zip) {
      addressValue += `${vendorData.contactInfo.country}, ${vendorData.contactInfo?.zip}`;
    } else {
      addressValue += vendorData ? `${vendorData.contactInfo.country}` : '';
    }

    return addressValue;
  };

  const handleRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setCurrPage(0);
  };

  useEffect(() => {
    if (!loadingUser && dataUser) {
      const fetchedVendorData = dataUser?.getVendorsByRepresentativeId?.[0];

      if (!fetchedVendorData) {
        alert(`You don't have access to the system. Please contact Carrier team`);
        logout();
      }

      setVendorData(fetchedVendorData);
      setRawProjectsList(dataUser?.getVendorsByRepresentativeId?.[0]?.projects ?? []);

      const isValid = isIdValid(currentUser?.username || '');

      if (isValid) {
        navigate(`${PrivateRoute.VendorDashboard}/${currentUser?.username}`, { replace: true });
      }
    }
  }, [dataUser]);

  useEffect(() => {
    if (vendorData?.projects) {
      const expiredProjectsCount =
        vendorData.projects?.filter(
          (project) =>
            project?.certificate && isProjectExpired(parseInt(project?.certificate?.expirationTime?.notAfter || '0')),
        )?.length ?? 0;
      const onboardingProjectsCount =
        vendorData.projects?.filter((project) => isProjectOnboarding(project?.status))?.length ?? 0;

      setVendorStats({
        onboardingPeopleCount: onboardingProjectsCount,
        expiredCertificatesCount: expiredProjectsCount,
      });
    }
  }, [vendorData]);

  useEffect(() => {
    if (currPage * itemsPerPage >= filteredProjects.length && filteredProjects.length > 0) {
      setCurrPage(Math.max(0, Math.ceil(filteredProjects.length / itemsPerPage) - 1));
    }
  }, [itemsPerPage, currPage, filteredProjects]);

  useEffect(() => {
    if ((currPage + 1) * itemsPerPage > filteredProjects.length) {
      setCurrPage(0);
    }
  }, [filteredProjects]);

  useHandleError(errorUser);

  return (
    <BaseLayout>
      <Stack direction="column" justifyContent="flex-start" className={styles.container}>
        {isLoaded ? (
          <>
            <Stack id="header" direction="column" style={{ marginLeft: '20px' }} spacing={2}>
              <Stack id="vendor" direction="row" justifyContent="flex-start" spacing={2}>
                <Box
                  data-testid="logo"
                  style={{
                    border: 'solid 1px #E0E0E0',
                    backgroundColor: '#FAFAFA',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <VendorDefLogoIcon />
                </Box>
                <Stack data-testid="details" direction="column" alignItems="flex-start" justifyContent="flex-start">
                  <Typography data-testid="vendor-title" variant="h2">
                    {vendorData && vendorData.vendorName} Connection Dashboard
                  </Typography>
                  <Typography data-testid="contact-info" variant="body3">
                    {getVendorAddress()}
                  </Typography>
                </Stack>
              </Stack>
              <Stack id="stats" direction="row" justifyContent="flex-start" spacing={3}>
                <StatBox
                  value={devicesStatsData?.getCountOfRegisteredDevices?.count ?? 0}
                  title="Devices registered"
                  accumulation="total"
                />
                <StatBox
                  value={vendorStats?.onboardingPeopleCount ?? 0}
                  title="Onboarding in progress"
                  accumulation="total"
                />
                <StatBox
                  value={
                    calcPublishedMessages(messagesStatsData?.getCountOfPublishedMessages?.vendorMessagesCount) ?? 0
                  }
                  title="Messages published"
                  accumulation="last 24 hours"
                />
                <StatBox
                  value={errorStatsData?.getErrorMessagesForProject?.errorCounter ?? 0}
                  title="Publish errors"
                  accumulation="last 24 hours"
                />
                <StatBox
                  value={vendorStats?.expiredCertificatesCount ?? 0}
                  title="Certificates expiring"
                  accumulation="next 60 days"
                />
              </Stack>
            </Stack>
            <Stack id="body" direction="column" className={styles.pageBody}>
              <Stack direction="column" style={{ marginLeft: '20px', marginRight: '25px' }}>
                <Stack direction="column" alignItems="flex-start" justifyContent="flex-start" spacing={2}>
                  <Typography variant="h5">Connected Data Producers</Typography>
                  <Typography variant="body1" style={{ textAlign: 'left' }}>
                    Here’s a overview of your connected data producers including device type, name, ID, and status. For
                    pending connections, complete the connection process via the provided button.
                  </Typography>
                </Stack>
                <Stack id="search" direction="row" justifyContent="space-between" style={{ marginTop: '15px' }}>
                  <SearchBox
                    multiple={false}
                    onChange={(event) => setProjectsSearch(event.text)}
                    sx={{
                      maxWidth: '450px',
                      width: '100%',
                    }}
                  />
                </Stack>
                <Stack id="vendorsGrid" direction="row">
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell align="left">
                            <Typography variant="tableHeader2">Device type</Typography>
                          </TableCell>
                          <TableCell align="left">
                            <Typography variant="tableHeader2">Data Producer Name</Typography>
                          </TableCell>
                          <TableCell align="left">
                            <Typography variant="tableHeader2">Device ID</Typography>
                          </TableCell>
                          <TableCell align="left">
                            <Typography variant="tableHeader2">Status</Typography>
                          </TableCell>
                          <TableCell align="center"></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedList?.map((project, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography>{project.type}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography>{`${project.name}`}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography>{project.id.toUpperCase()}</Typography>
                            </TableCell>
                            <TableCell>{MapStatusToComponent[project.status]}</TableCell>
                            <TableCell>
                              {isTeamLead && isConnectionButtonShown(project, currentUser.username) ? (
                                <Button
                                  data-testid="complete-connection"
                                  onClick={() => completeConnection(project.id, project.status)}
                                >
                                  Complete Connection
                                </Button>
                              ) : (
                                <div></div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TablePagination
                            data-testid="table-pagination"
                            count={filteredProjects.length}
                            onPageChange={(_, page) => setCurrPage(page)}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            page={currPage}
                            rowsPerPage={itemsPerPage}
                            rowsPerPageOptions={[1, 5, 10, 25]}
                            sx={{
                              width: '1000px',
                            }}
                          />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                </Stack>
              </Stack>
            </Stack>
          </>
        ) : (
          <Loader />
        )}
      </Stack>
    </BaseLayout>
  );
};

export default VendorDashboard;
